import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { CookieConsentProvider } from '@/contexts/CookieConsentContext';
import { ToastProvider } from '@/components/ui';
import { ProtectedRoute, AdminRoute } from '@/components/auth';
import { CookieBanner } from '@/components/consent';

// Pages
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
// Register disabled (waiting list mode)
// import Register from '@/pages/Register';
import { Navigate } from 'react-router-dom';
import ForgotPassword from '@/pages/ForgotPassword';
import Dashboard from '@/pages/Dashboard';
import Deeplinks from '@/pages/Deeplinks';
import DeeplinkEdit from '@/pages/DeeplinkEdit';
import ProfileEditor from '@/pages/ProfileEditor';
import DynamicRoute from '@/pages/DynamicRoute';
import QRCodes from '@/pages/QRCodes';
import Stats from '@/pages/Stats';
import Pixel from '@/pages/Pixel';
import Settings from '@/pages/Settings';
import Contents from '@/pages/Contents';
import Help from '@/pages/Help';
import LinkPages from '@/pages/LinkPages';
import LinkPageEditor from '@/pages/LinkPageEditor';
import Terms from '@/pages/Terms';
import Legal from '@/pages/Legal';
import Privacy from '@/pages/Privacy';
import Cookies from '@/pages/Cookies';
import ReportAbuse from '@/pages/ReportAbuse';
import NotFound from '@/pages/NotFound';

// Admin pages
import { AdminDashboard, AdminUsers, AdminDeeplinks, AdminWhitelist } from '@/pages/admin';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <CookieConsentProvider>
            <Router>
            <CookieBanner />
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/" replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/report-abuse" element={<ReportAbuse />} />

            {/* Dynamic route: @username → profile, code → deeplink redirect */}
            <Route path="/:param" element={<DynamicRoute />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deeplinks"
              element={
                <ProtectedRoute>
                  <Deeplinks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/deeplinks/:id"
              element={
                <ProtectedRoute>
                  <DeeplinkEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfileEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/qr-codes"
              element={
                <ProtectedRoute>
                  <QRCodes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/stats"
              element={
                <ProtectedRoute>
                  <Stats />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pixel"
              element={
                <ProtectedRoute>
                  <Pixel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contents"
              element={
                <ProtectedRoute>
                  <Contents />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/linkpages"
              element={
                <ProtectedRoute>
                  <LinkPages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/linkpages/:id"
              element={
                <ProtectedRoute>
                  <LinkPageEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminRoute>
                  <AdminUsers />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/deeplinks"
              element={
                <AdminRoute>
                  <AdminDeeplinks />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/whitelist"
              element={
                <AdminRoute>
                  <AdminWhitelist />
                </AdminRoute>
              }
            />

            {/* 404 - Page not found */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
            </Router>
          </CookieConsentProvider>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
